import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { verifyBusiness, selectVerification } from "../../store/slices/aiSlice";
import { ShieldCheck, ShieldX, Loader2, Building2, Landmark, CreditCard } from "lucide-react";
import toast from "react-hot-toast";
import AIPanel, { AI_BTN, AI_INPUT } from "./AIPanel";

function StatusBadge({ verified, partial }) {
  const base = "inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border";
  if (verified && !partial) return <span className={`${base} text-emerald-700 bg-emerald-50 border-emerald-200`}><ShieldCheck size={12} /> Verified</span>;
  if (verified && partial) return <span className={`${base} text-amber-700 bg-amber-50 border-amber-200`}><ShieldCheck size={12} /> Partially Verified</span>;
  return <span className={`${base} text-gray-600 bg-gray-50 border-gray-200`}><ShieldX size={12} /> Not Verified</span>;
}

function VerificationSection({ title, hint, icon: Icon, verified, partial, children }) {
  return (
    <div className="rounded-2xl border border-gray-200/70 bg-gray-50/60 p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white border border-gray-200/70">
            <Icon size={18} className="text-purple-600" />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-bold text-gray-900">{title}</h4>
            {hint && <p className="text-xs text-gray-500">{hint}</p>}
          </div>
        </div>
        <StatusBadge verified={verified} partial={partial} />
      </div>
      {children}
    </div>
  );
}

const RESULT_BOX = "mt-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-xs text-gray-700 space-y-1";

export default function AIBusinessVerifier({ vendor }) {
  const dispatch = useDispatch();
  const { loading } = useSelector(selectVerification);

  const [gstin, setGstin] = useState(vendor?.verification?.gstin?.number || "");
  const [pan, setPan] = useState(vendor?.verification?.pan?.number || "");
  const [bankAcc, setBankAcc] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [holderName, setHolderName] = useState("");
  const [results, setResults] = useState({});

  const handleVerify = async (type, payload) => {
    try {
      const result = await dispatch(verifyBusiness({ type, ...payload })).unwrap();
      setResults((prev) => ({ ...prev, [type]: result }));
      if (result.verified) toast.success(`${type.toUpperCase()} verified!`);
      else toast.error(result.error || `${type.toUpperCase()} verification failed`);
    } catch (err) {
      toast.error(err || "Verification failed");
    }
  };

  const gstVerified = results.gstin?.verified || vendor?.verification?.gstin?.verified;
  const bankVerified = results.bank?.verified || vendor?.verification?.bankAccount?.verified;
  const panVerified = results.pan?.verified || vendor?.verification?.pan?.verified;

  const inputClass = AI_INPUT;
  const btnClass = `${AI_BTN} flex-shrink-0`;

  return (
    <AIPanel
      icon={ShieldCheck}
      title="Business Verification"
      description="Verify your tax and bank details to build trust with customers."
    >
      <div className="space-y-4">
        {/* GSTIN */}
        <VerificationSection title="GSTIN" hint="15-character GST number" icon={Building2} verified={gstVerified}>
          <div className="flex gap-2">
            <input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" maxLength={15} className={inputClass} />
            <button onClick={() => handleVerify("gstin", { gstin })} disabled={loading || gstin.length !== 15} className={btnClass}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : "Verify"}
            </button>
          </div>
          {results.gstin?.data && (
            <div className={RESULT_BOX}>
              {results.gstin.data.legalName && <p><strong>Legal Name:</strong> {results.gstin.data.legalName}</p>}
              {results.gstin.data.tradeName && <p><strong>Trade Name:</strong> {results.gstin.data.tradeName}</p>}
              {results.gstin.data.status && <p><strong>Status:</strong> {results.gstin.data.status}</p>}
            </div>
          )}
        </VerificationSection>

        {/* Bank Account */}
        <VerificationSection title="Bank Account" hint="Account number and IFSC code" icon={Landmark} verified={bankVerified} partial={results.bank?.partial}>
          <div className="space-y-2">
            <input value={holderName} onChange={(e) => setHolderName(e.target.value)} placeholder="Account holder name" className={inputClass} />
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={bankAcc} onChange={(e) => setBankAcc(e.target.value)} placeholder="Account number" className={inputClass} />
              <input value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} placeholder="IFSC code" maxLength={11} className={`${inputClass} sm:w-44`} />
            </div>
            <button onClick={() => handleVerify("bank", { accountNumber: bankAcc, ifscCode: ifsc, accountHolderName: holderName })} disabled={loading || !bankAcc || !ifsc} className={btnClass}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : "Verify Account"}
            </button>
          </div>
          {results.bank?.data && (
            <div className={RESULT_BOX}>
              {results.bank.data.bankName && <p><strong>Bank:</strong> {results.bank.data.bankName}</p>}
              {results.bank.data.branch && <p><strong>Branch:</strong> {results.bank.data.branch}</p>}
              {results.bank.data.nameMatch && <p><strong>Name on Account:</strong> {results.bank.data.nameMatch}</p>}
              {results.bank.data.note && <p className="text-amber-600">{results.bank.data.note}</p>}
            </div>
          )}
        </VerificationSection>

        {/* PAN */}
        <VerificationSection title="PAN" hint="10-character PAN" icon={CreditCard} verified={panVerified} partial={results.pan?.partial}>
          <div className="flex gap-2">
            <input value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} className={inputClass} />
            <button onClick={() => handleVerify("pan", { pan })} disabled={loading || pan.length !== 10} className={btnClass}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : "Verify"}
            </button>
          </div>
          {results.pan?.data && (
            <div className={RESULT_BOX}>
              <p><strong>Type:</strong> {results.pan.data.type}</p>
              {results.pan.data.note && <p className="text-amber-600">{results.pan.data.note}</p>}
            </div>
          )}
        </VerificationSection>
      </div>
    </AIPanel>
  );
}
